import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subject } from 'rxjs';
import { AuthorizationService } from 'src/app/auth/services/authorization.service';
import { StandardNamn } from 'src/app/shared/models/standard-namn';
import { ButtonSetItem } from '../../shared/components/app-button-set/app-button-set-models';
import { TableConfig } from '../../shared/components/app-table/app-table-models';
import { NameType } from '../../shared/models/models';
import { Role } from '../anvandare/anvandare.models';
import { StandardNamnEditDialogComponent } from './standard-namn-edit-dialog/standard-namn-edit-dialog.component';
import { StandardNamnService } from './standard-namn.service';
import { StandardNamnStatus } from 'src/app/shared/models/standard-namn';

@Component({
    selector: 'lib-standardnamn',
    templateUrl: './standard-namn.component.html',
    styleUrls: ['./standard-namn.component.css'],
    standalone: false
})
export class StandardNamnComponent {
  private standardNamnService = inject(StandardNamnService);
  private authorizationService = inject(AuthorizationService);
  private dialogService = inject(DialogService);
  private destroyRef = inject(DestroyRef);

  protected tableConfig = this.getTableConfig();
  private ref: DynamicDialogRef | null = null;
  protected savedRowId: string | null = null;


  protected standardNamnTypesButtonSet: ButtonSetItem[] = [
    { text: 'Smittämne', value: NameType.Smittamne, active: true },
    { text: 'Analys', value: NameType.Analys },
    { text: 'Provmaterial', value: NameType.Provmaterial },
    { text: 'Antibiotika', value: NameType.Antibiotika },
    { text: 'Negativt fynd', value: NameType.NegativtFynd },
    { text: 'Beskrivande fynd', value: NameType.BeskrivandeFynd },
  ];

  ngOnInit() {
    this.standardNamnService.load();
    this.getStandardnamn(NameType.Smittamne);
  }

  getTableConfig(): TableConfig {
    const tableConfig: TableConfig = {
      multiSortMeta: [
        { field: 'status', order: 1 },
        { field: 'standardNamn', order: 1 },
      ],
      columns: [
        {
          field: 'standardNamn',
          header: 'Namn',
          sortByField: 'standardNamn',
          columnFilter: 'text',
        },
        {
          field: 'startDatum',
          header: 'Skapad datum',
          sortByField: 'startDatum',
          mapView: (row: string) => `${new Date(row).toLocaleString()}`,
          columnFilter: 'text',
        },
        {
          field: 'slutDatum',
          header: 'Till tidpunkt',
          sortByField: 'slutDatum',
          mapView: (row: string) => (row ? `${new Date(row).toLocaleString()}` : 'Tills vidare'),
          columnFilter: 'text',
        },

        {
          field: 'modifiedBy',
          header: 'Modifierad av',
          sortByField: 'modifiedBy',
          mapView: (row: string) => (row ? row : 'System Import'),
          columnFilter: 'text',
        },
        {
          field: 'status',
          header: 'Status',
          sortByField: 'status',
          columnFilter: 'multi-select',
          filterOptions: [
            { name: 'AKTIV', label: 'Aktiv' },
            { name: 'INAKTIV', label: 'Inaktiv' },
          ],
        },
        {
          field: 'family',
          header: 'Tillhör',
          sortByField: 'family',
          columnFilter: 'text',
        },
        {
          field: 'fhomStandardName',
          header: 'Fohm standardnamn',
          sortByField: 'fhomStandardName',
          columnFilter: 'text',
        },
      ],
    };

    if (this.authorizationService.hasRole([Role.SuperanvandareIntern])) {
      tableConfig.columns?.push(
        {
          field: '',
          header: '',
          type: 'editButton',
          click: (e) => this.onEdit(e),
          editableCondition: {field: 'canEdit', value: 'true',},

        },

      );

    }
  return tableConfig;

  }

  private onEdit(standardNamnObject: any) {
    const dialogRef = this.dialogService.open(StandardNamnEditDialogComponent, {
      header: 'Ändra standardnamn',
      width: '750px',
      contentStyle: { overflow: 'visible' },
      baseZIndex: 10000,
      maximizable: false,
      data: {
        standardNamnObject,
      },
    });

    if (!dialogRef) {
      return;
    }

    this.ref = dialogRef;

    dialogRef.onClose.subscribe((modifiedName: StandardNamnStatus) => {
      if (!modifiedName) {
        return; // Cancel
      }

      const nameType = this.standardNamnTypesButtonSet
        .find(set => set.active)?.value;

      this.standardNamnService.load(true);
      this.getStandardnamn(nameType);
    });
  }
  protected onSelect(item: ButtonSetItem) {
    this.getStandardnamn(item.value);
  }

  private getStandardnamn(type: NameType) {
    this.tableConfig.rows = [];

    this.standardNamnService
      .getStandardNamnByType(type)
      ?.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((nameList) => {
        nameList.forEach((name: any) => {
          name.type = type;
          name.canEdit =
            name.family === 'Svebar' && type === NameType.Smittamne
              ? 'true'
              : 'false';;
        });

        this.tableConfig.rows = nameList;
      });
  }

}
